// ===================================
// TRENCH QUEST - Market Engine
// Simulates price movements for tokens
// ===================================

import { ALL_TOKENS, getToken, type TokenData } from '../tokens/data'
import type { MarketCondition } from '@/types'

export interface TokenPrice {
  symbol: string
  price: number
  change24h: number // Percentage change
  change7d: number
  volume24h: number
  marketCap: number
  lastUpdate: number // Timestamp
}

export interface MarketState {
  prices: Record<string, TokenPrice>
  condition: MarketCondition
  fearGreedIndex: number // 0-100, 0 = extreme fear, 100 = extreme greed
  lastMajorEvent: string | null
  ruggedTokens: string[] // Tokens that have been rugged
}

export interface MarketEvent {
  type: 'pump' | 'dump' | 'rug' | 'news' | 'whale' | 'listing' | 'fud' | 'shill'
  token?: string
  magnitude: number // 0-1, how impactful
  headline: string
  description: string
}

// Seed random for reproducibility in sessions
let seed = Date.now()
const seededRandom = (): number => {
  seed = (seed * 1103515245 + 12345) & 0x7fffffff
  return seed / 0x7fffffff
}

export const setSeed = (newSeed: number) => {
  seed = newSeed
}

// Initialize market with base prices
export const initializeMarket = (difficulty: string): MarketState => {
  const prices: Record<string, TokenPrice> = {}

  // Difficulty affects starting conditions
  const difficultyMultiplier = difficulty === 'paper_hands' ? 1.1 : difficulty === 'gigabrain' ? 0.9 : 1.0

  for (const token of ALL_TOKENS) {
    const baseVariance = (seededRandom() - 0.5) * 0.2 // +/- 10%
    const price = token.basePrice * (1 + baseVariance) * difficultyMultiplier

    prices[token.symbol] = {
      symbol: token.symbol,
      price: token.symbol === 'USDC' ? 1.0 : price,
      change24h: 0,
      change7d: 0,
      volume24h: seededRandom() * 10000000,
      marketCap: price * (token.maxSupply || 1000000000),
      lastUpdate: Date.now(),
    }
  }

  return {
    prices,
    condition: 'crab',
    fearGreedIndex: 50,
    lastMajorEvent: null,
    ruggedTokens: [],
  }
}

// Calculate price change for a single token
const calculatePriceChange = (
  token: TokenData,
  currentPrice: number,
  marketCondition: MarketCondition,
  fearGreedIndex: number,
  hoursElapsed: number
): number => {
  if (token.symbol === 'USDC') return 1.0 // Stablecoin doesn't move

  // Base volatility movement
  const randomFactor = (seededRandom() - 0.5) * 2 // -1 to 1
  const volatilityMove = randomFactor * token.volatility * (hoursElapsed / 24)

  // Market condition bias
  const conditionBias = marketCondition === 'bull' ? 0.02 : marketCondition === 'bear' ? -0.02 : 0

  // Fear/greed affects momentum
  const sentimentBias = (fearGreedIndex - 50) / 5000 // Small bias based on sentiment

  // Category-specific behavior
  const categoryMultipliers: Record<string, number> = {
    stable: 0.001,
    blue_chip: 0.8,
    mid_cap: 1.2,
    degen: 1.8,
    ultra_degen: 2.5,
  }
  const categoryMultiplier = categoryMultipliers[token.category] || 1.0

  // Calculate total move
  const totalMove = (volatilityMove + conditionBias + sentimentBias) * categoryMultiplier

  // Apply movement with floor at near-zero
  const newPrice = currentPrice * (1 + totalMove)
  return Math.max(newPrice, currentPrice * 0.001) // Can't go below 0.1% of current
}

// Simulate market for a time period
export const simulateMarket = (
  market: MarketState,
  hoursElapsed: number,
  difficulty: string
): { market: MarketState; events: MarketEvent[] } => {
  const events: MarketEvent[] = []
  const newPrices = { ...market.prices }
  let newCondition = market.condition
  let newFearGreed = market.fearGreedIndex

  // Process in chunks of 1 hour
  const chunks = Math.ceil(hoursElapsed)

  for (let i = 0; i < chunks; i++) {
    const chunkHours = Math.min(1, hoursElapsed - i)

    // Chance of market condition change
    if (seededRandom() < 0.05) {
      const conditions: MarketCondition[] = ['bull', 'bear', 'crab']
      const randomIndex = Math.floor(seededRandom() * 3)
      newCondition = conditions[randomIndex] || 'crab'
      events.push({
        type: 'news',
        magnitude: 0.5,
        headline: getMarketConditionHeadline(newCondition),
        description: getMarketConditionDescription(newCondition),
      })
    }

    // Chance of random events
    const eventChances: Record<string, number> = {
      paper_hands: 0.02,
      diamond_hands: 0.05,
      gigabrain: 0.08,
    }
    const eventChance = eventChances[difficulty] || 0.05

    if (seededRandom() < eventChance) {
      const event = generateRandomEvent(market, difficulty)
      if (event) {
        events.push(event)
        applyEventToMarket(event, newPrices, market.ruggedTokens)
      }
    }

    // Update all prices
    for (const token of ALL_TOKENS) {
      const tokenPrice = newPrices[token.symbol]
      const oldTokenPrice = market.prices[token.symbol]

      if (!tokenPrice || !oldTokenPrice) continue

      if (market.ruggedTokens.includes(token.symbol)) {
        tokenPrice.price = 0.0000001
        continue
      }

      const currentPrice = tokenPrice.price
      const oldPrice = oldTokenPrice.price
      const newPrice = calculatePriceChange(
        token,
        currentPrice,
        newCondition,
        newFearGreed,
        chunkHours
      )

      newPrices[token.symbol] = {
        ...tokenPrice,
        price: newPrice,
        change24h: oldPrice > 0 ? ((newPrice - oldPrice) / oldPrice) * 100 : 0,
        lastUpdate: Date.now(),
      }
    }

    // Fear/greed drift
    const fearGreedDrift = (seededRandom() - 0.5) * 10
    newFearGreed = Math.max(0, Math.min(100, newFearGreed + fearGreedDrift))
  }

  const lastEvent = events[events.length - 1]
  return {
    market: {
      ...market,
      prices: newPrices,
      condition: newCondition,
      fearGreedIndex: newFearGreed,
      lastMajorEvent: lastEvent ? lastEvent.headline : market.lastMajorEvent,
    },
    events,
  }
}

// Generate random market event
const generateRandomEvent = (
  market: MarketState,
  difficulty: string
): MarketEvent | null => {
  const roll = seededRandom()

  // Get a random non-stable token
  const tradableTokens = ALL_TOKENS.filter(
    t => t.category !== 'stable' && !market.ruggedTokens.includes(t.symbol)
  )

  if (tradableTokens.length === 0) return null

  const randomIndex = Math.floor(seededRandom() * tradableTokens.length)
  const randomToken = tradableTokens[randomIndex]

  if (!randomToken) return null

  // Event probabilities based on difficulty
  const rugChances: Record<string, number> = {
    paper_hands: 0.02,
    diamond_hands: 0.05,
    gigabrain: 0.10,
  }
  const rugChance = rugChances[difficulty] || 0.05

  // Rug pull (only for degen/ultra_degen tokens)
  if (roll < rugChance && ['degen', 'ultra_degen'].includes(randomToken.category)) {
    if (seededRandom() < randomToken.rugRisk) {
      return {
        type: 'rug',
        token: randomToken.symbol,
        magnitude: 1.0,
        headline: `${randomToken.symbol} RUGGED - Dev Sold Everything`,
        description: `The anonymous dev behind ${randomToken.name} just drained the liquidity pool. Classic rug. Many such cases.`,
      }
    }
  }

  // Pump
  if (roll < 0.3) {
    const pumpIndex = Math.floor(seededRandom() * PUMP_DESCRIPTIONS.length)
    const pumpDesc = PUMP_DESCRIPTIONS[pumpIndex] || '{token} is pumping hard.'
    return {
      type: 'pump',
      token: randomToken.symbol,
      magnitude: seededRandom() * 0.5 + 0.2,
      headline: `${randomToken.symbol} Pumping Hard`,
      description: pumpDesc.replace('{token}', randomToken.symbol),
    }
  }

  // Dump
  if (roll < 0.5) {
    const dumpIndex = Math.floor(seededRandom() * DUMP_DESCRIPTIONS.length)
    const dumpDesc = DUMP_DESCRIPTIONS[dumpIndex] || '{token} is dumping hard.'
    return {
      type: 'dump',
      token: randomToken.symbol,
      magnitude: seededRandom() * 0.4 + 0.1,
      headline: `${randomToken.symbol} Taking a Dive`,
      description: dumpDesc.replace('{token}', randomToken.symbol),
    }
  }

  // Whale activity
  if (roll < 0.7) {
    const isBuying = seededRandom() > 0.5
    return {
      type: 'whale',
      token: randomToken.symbol,
      magnitude: seededRandom() * 0.3 + 0.1,
      headline: `Whale ${isBuying ? 'Accumulating' : 'Dumping'} ${randomToken.symbol}`,
      description: isBuying
        ? `A wallet with deep pockets just scooped up a massive bag of ${randomToken.symbol}. Smart money?`
        : `Major holder just market sold a huge ${randomToken.symbol} position. Weak hands or insider info?`,
    }
  }

  // Influencer shill
  if (roll < 0.85) {
    const shillIndex = Math.floor(seededRandom() * SHILL_DESCRIPTIONS.length)
    const shillDesc = SHILL_DESCRIPTIONS[shillIndex] || '{token} is being shilled.'
    return {
      type: 'shill',
      token: randomToken.symbol,
      magnitude: seededRandom() * 0.3 + 0.1,
      headline: `CT Influencer Shilling ${randomToken.symbol}`,
      description: shillDesc.replace('{token}', randomToken.symbol),
    }
  }

  // FUD
  const fudIndex = Math.floor(seededRandom() * FUD_DESCRIPTIONS.length)
  const fudDesc = FUD_DESCRIPTIONS[fudIndex] || '{token} FUD spreading.'
  return {
    type: 'fud',
    token: randomToken.symbol,
    magnitude: seededRandom() * 0.2 + 0.1,
    headline: `FUD Spreading About ${randomToken.symbol}`,
    description: fudDesc.replace('{token}', randomToken.symbol),
  }
}

// Apply event effects to market
const applyEventToMarket = (
  event: MarketEvent,
  prices: Record<string, TokenPrice>,
  ruggedTokens: string[]
) => {
  if (!event.token) return

  const tokenPrice = prices[event.token]
  if (!tokenPrice) return

  switch (event.type) {
    case 'rug':
      tokenPrice.price = 0.0000001
      tokenPrice.change24h = -99.99
      ruggedTokens.push(event.token)
      break
    case 'pump':
      tokenPrice.price *= 1 + event.magnitude
      tokenPrice.change24h += event.magnitude * 100
      break
    case 'dump':
      tokenPrice.price *= 1 - event.magnitude * 0.5
      tokenPrice.change24h -= event.magnitude * 50
      break
    case 'whale':
      tokenPrice.price *= seededRandom() > 0.5 ? 1 + event.magnitude * 0.3 : 1 - event.magnitude * 0.2
      break
    case 'shill':
      tokenPrice.price *= 1 + event.magnitude * 0.4
      tokenPrice.change24h += event.magnitude * 40
      break
    case 'fud':
      tokenPrice.price *= 1 - event.magnitude * 0.3
      tokenPrice.change24h -= event.magnitude * 30
      break
  }
}

// Headline generators
const getMarketConditionHeadline = (condition: MarketCondition): string => {
  const headlines: Record<MarketCondition, string[]> = {
    bull: [
      'Market Sentiment Shifts Bullish',
      'Risk-On Mode Activated',
      'Green Candles Everywhere',
      'CT Goes Full Bull Mode',
    ],
    bear: [
      'Bears Take Control',
      'Market Sentiment Turns Sour',
      'Red Across The Board',
      'Panic Selling Begins',
    ],
    crab: [
      'Market Goes Sideways',
      'Boring Price Action Continues',
      'Neither Bulls Nor Bears Winning',
      'Accumulation Phase?',
    ],
  }
  const options = headlines[condition]
  const index = Math.floor(seededRandom() * options.length)
  return options[index] || options[0] || 'Market Update'
}

const getMarketConditionDescription = (condition: MarketCondition): string => {
  const descriptions: Record<MarketCondition, string> = {
    bull: 'Buyers are stepping in aggressively. Every dip getting bought. This is the energy that makes millionaires.',
    bear: 'Sellers in control. Support levels breaking. Diamond hands being tested. NGMI energy spreading.',
    crab: 'Price going nowhere. Perfect time to accumulate... or get chopped up trying to trade the range.',
  }
  return descriptions[condition]
}

const PUMP_DESCRIPTIONS = [
  '{token} just broke out of a 4-hour consolidation. Momentum traders are piling in.',
  'Rumor spreading that a major CEX is about to list {token}. Unconfirmed but CT is running with it.',
  '{token} community just announced a major partnership. Details sketchy but the chart speaks.',
  'Short squeeze on {token}. Overleveraged bears getting liquidated.',
  'New narrative forming around {token}. Early believers being rewarded.',
]

const DUMP_DESCRIPTIONS = [
  '{token} breaking down from key support. Stop losses triggering.',
  'Large unlock of {token} tokens hitting the market. Team selling?',
  '{token} whale wallet moving tokens to exchange. Dump incoming.',
  'Negative sentiment spreading about {token}. Paper hands folding.',
  '{token} dev wallet showing suspicious activity. Community on edge.',
]

const SHILL_DESCRIPTIONS = [
  'A 500k follower account just posted a thread on why {token} is the next 100x.',
  'Multiple influencers posting about {token} simultaneously. Coordinated or coincidence?',
  'Famous trader shows {token} entry with massive position size.',
  '{token} getting mentioned in every space. The narrative is forming.',
  'Paid promo? Organic discovery? Either way, {token} is trending.',
]

const FUD_DESCRIPTIONS = [
  'Anonymous account posting "research" thread claiming {token} is a scam.',
  'Competitor project spreading rumors about {token} dev team.',
  '{token} contract getting scrutinized. "Experts" finding "concerns".',
  'Old FUD from 3 months ago resurfacing about {token}. Classic cycle.',
  'Random blue check claiming insider info that {token} is about to dump.',
]

// Execute a trade and return new prices (includes slippage)
export const executeTrade = (
  market: MarketState,
  fromToken: string,
  toToken: string,
  fromAmount: number,
  slippageTolerance: number = 0.01
): { toAmount: number; executionPrice: number; slippage: number; newMarket: MarketState } => {
  const fromPrice = market.prices[fromToken]?.price || 1
  const toPrice = market.prices[toToken]?.price || 1

  // Calculate base amount
  const usdValue = fromAmount * fromPrice
  const baseToAmount = usdValue / toPrice

  // Calculate slippage based on trade size and token volatility
  const fromTokenData = getToken(fromToken)
  const toTokenData = getToken(toToken)
  const avgVolatility = ((fromTokenData?.volatility || 0.1) + (toTokenData?.volatility || 0.1)) / 2

  // Larger trades = more slippage
  const sizeImpact = Math.min(usdValue / 100000, 0.05) // Max 5% from size
  const volatilityImpact = avgVolatility * 0.1 // Up to 10% from volatility
  const randomSlippage = seededRandom() * 0.02 // Random 0-2%

  const totalSlippage = Math.min(sizeImpact + volatilityImpact + randomSlippage, slippageTolerance)
  const toAmount = baseToAmount * (1 - totalSlippage)
  const executionPrice = usdValue / toAmount

  // Update market prices slightly based on trade (price impact)
  const newPrices = { ...market.prices }

  const fromTokenPrice = newPrices[fromToken]
  const toTokenPrice = newPrices[toToken]

  if (fromToken !== 'USDC' && fromTokenPrice) {
    newPrices[fromToken] = {
      ...fromTokenPrice,
      price: fromTokenPrice.price * (1 - sizeImpact * 0.1),
    }
  }
  if (toToken !== 'USDC' && toTokenPrice) {
    newPrices[toToken] = {
      ...toTokenPrice,
      price: toTokenPrice.price * (1 + sizeImpact * 0.1),
    }
  }

  return {
    toAmount,
    executionPrice,
    slippage: totalSlippage,
    newMarket: { ...market, prices: newPrices },
  }
}

// Get price in USDC
export const getPriceInUSDC = (market: MarketState, symbol: string): number => {
  return market.prices[symbol]?.price || 0
}

// Calculate portfolio value
export const calculatePortfolioValue = (
  holdings: { symbol: string; amount: number }[],
  market: MarketState
): number => {
  return holdings.reduce((total, holding) => {
    const price = getPriceInUSDC(market, holding.symbol)
    return total + holding.amount * price
  }, 0)
}
